// frontend/src/components/Events/CreateEditNewsModal.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  FormHelperText,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  alpha,
  useTheme,
  Grid,
  Card,
  CardMedia,
  CardActions
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Movie as MovieIcon,
  Image as ImageIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Article as ArticleIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useDropzone } from 'react-dropzone';
import { newsAPI } from '../../services/eventsAPI';

// ============================================
// СТИЛИЗОВАННЫЕ КОМПОНЕНТЫ — iOS 26 / Glass
// ============================================

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-container': {
    padding: theme.spacing(2),
  },
  '& .MuiDialog-paper': {
    width: 'min(860px, calc(100vw - 32px))',
    maxWidth: 860,
    minWidth: 0,
    maxHeight: 'calc(100vh - 32px)',
    margin: 0,
    overflow: 'hidden',
    borderRadius: theme.spacing(4.5),
    border: `1px solid ${alpha(theme.palette.common.white, 0.82)}`,
    background: `
      linear-gradient(145deg,
        ${alpha(theme.palette.common.white, 0.74)},
        ${alpha(theme.palette.grey[100], 0.46)} 46%,
        ${alpha(theme.palette.primary.main, 0.09)}
      ),
      radial-gradient(circle at 8% 4%, ${alpha(theme.palette.info.light, 0.16)}, transparent 34%),
      radial-gradient(circle at 92% 90%, ${alpha(theme.palette.primary.main, 0.15)}, transparent 42%)
    `,
    backgroundColor: alpha(theme.palette.common.white, 0.56),
    backdropFilter: 'blur(30px) saturate(150%)',
    WebkitBackdropFilter: 'blur(30px) saturate(150%)',
    boxShadow: `0 28px 90px ${alpha(theme.palette.common.black, 0.22)}`,
  },
  '& .MuiBackdrop-root': {
    backgroundColor: alpha(theme.palette.common.black, 0.32),
    // Фон страницы остаётся резким: блюр используется только внутри самого окна.
    backdropFilter: 'none',
    WebkitBackdropFilter: 'none',
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  padding: theme.spacing(2.6, 3),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexShrink: 0,
  background: `linear-gradient(135deg, ${alpha(theme.palette.common.white, 0.48)}, ${alpha(theme.palette.common.white, 0.14)})`,
  borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.62)}`,
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  padding: theme.spacing(2.8, 3),
  background: `linear-gradient(180deg, ${alpha(theme.palette.common.white, 0.16)}, ${alpha(theme.palette.primary.main, 0.025)})`,
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: alpha(theme.palette.common.white, 0.32),
    borderRadius: '999px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.text.secondary, 0.24),
    borderRadius: '999px',
    '&:hover': {
      background: alpha(theme.palette.primary.main, 0.48),
    },
  },
}));

const GlassField = ({ children, sx, ...props }) => {
  const theme = useTheme();

  return (
    <TextField
      {...props}
      sx={{
        '& .MuiInputLabel-root': {
          fontWeight: 450,
          color: 'text.secondary',
        },
        '& .MuiInputLabel-root.Mui-focused': {
          color: 'primary.main',
        },
        '& .MuiOutlinedInput-root': {
          borderRadius: 3,
          background: `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.58)}, ${alpha(theme.palette.common.white, 0.26)})`,
          backdropFilter: 'blur(14px) saturate(130%)',
          WebkitBackdropFilter: 'blur(14px) saturate(130%)',
          transition: 'border-color 180ms ease, box-shadow 180ms ease, background-color 180ms ease',
          '& fieldset': {
            borderColor: alpha(theme.palette.common.white, 0.78),
          },
          '&:hover fieldset': {
            borderColor: alpha(theme.palette.primary.main, 0.28),
          },
          '&.Mui-focused': {
            backgroundColor: alpha(theme.palette.common.white, 0.62),
            boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.08)}`,
          },
          '&.Mui-focused fieldset': {
            borderColor: alpha(theme.palette.primary.main, 0.56),
            borderWidth: 1,
          },
        },
        ...sx,
      }}
    >
      {children}
    </TextField>
  );
};

const DropzoneArea = styled(Paper)(({ theme, isdragactive }) => ({
  padding: theme.spacing(3.2),
  textAlign: 'center',
  cursor: 'pointer',
  width: '100%',
  borderRadius: theme.spacing(3.5),
  border: `1.5px dashed ${isdragactive === 'true' ? alpha(theme.palette.primary.main, 0.72) : alpha(theme.palette.common.white, 0.82)}`,
  background: `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.52)}, ${alpha(theme.palette.common.white, 0.18)}), radial-gradient(circle at 50% 0%, ${alpha(theme.palette.primary.main, 0.08)}, transparent 48%)`,
  backgroundColor: alpha(theme.palette.common.white, 0.36),
  backdropFilter: 'blur(18px) saturate(135%)',
  WebkitBackdropFilter: 'blur(18px) saturate(135%)',
  boxShadow: `0 12px 34px ${alpha(theme.palette.common.black, 0.06)}`,
  transition: 'transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease, background-color 180ms ease',
  '&:hover': {
    borderColor: alpha(theme.palette.primary.main, 0.54),
    backgroundColor: alpha(theme.palette.common.white, 0.48),
    boxShadow: `0 16px 40px ${alpha(theme.palette.common.black, 0.09)}`,
    transform: 'translateY(-1px)',
  },
}));

const MediaCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2.8),
  overflow: 'hidden',
  position: 'relative',
  border: `1px solid ${alpha(theme.palette.common.white, 0.78)}`,
  background: `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.58)}, ${alpha(theme.palette.common.white, 0.22)})`,
  backgroundColor: alpha(theme.palette.common.white, 0.38),
  backdropFilter: 'blur(16px) saturate(130%)',
  WebkitBackdropFilter: 'blur(16px) saturate(130%)',
  boxShadow: `0 10px 30px ${alpha(theme.palette.common.black, 0.08)}`,
  '&:hover .delete-overlay': {
    opacity: 1,
  },
}));

const DeleteOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  background: `linear-gradient(145deg, ${alpha(theme.palette.common.black, 0.12)}, ${alpha(theme.palette.common.black, 0.48)})`,
  backdropFilter: 'blur(4px)',
  WebkitBackdropFilter: 'blur(4px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0,
  transition: 'opacity 180ms ease',
  zIndex: 1,
}));

const GlassSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(3),
  border: `1px solid ${alpha(theme.palette.common.white, 0.72)}`,
  background: `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.42)}, ${alpha(theme.palette.common.white, 0.16)}), radial-gradient(circle at 100% 0%, ${alpha(theme.palette.primary.main, 0.055)}, transparent 45%)`,
  backgroundColor: alpha(theme.palette.common.white, 0.28),
  backdropFilter: 'blur(16px) saturate(130%)',
  WebkitBackdropFilter: 'blur(16px) saturate(130%)',
  boxShadow: `0 8px 28px ${alpha(theme.palette.common.black, 0.045)}`,
}));

const SectionLabel = ({ children }) => {
  const theme = useTheme();
  return (
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        mb: 1,
        color: 'text.secondary',
        fontWeight: 600,
        fontSize: '0.72rem',
        letterSpacing: 0.45,
        textTransform: 'uppercase',
      }}
    >
      {children}
    </Typography>
  );
};

const GlassButton = styled(Button)(({ theme }) => ({
  borderRadius: 999,
  textTransform: 'none',
  minHeight: 44,
  paddingLeft: theme.spacing(2.5),
  paddingRight: theme.spacing(2.5),
  fontWeight: 650,
  color: theme.palette.text.primary,
  backgroundColor: alpha(theme.palette.common.white, 0.48),
  border: `1px solid ${alpha(theme.palette.common.white, 0.84)}`,
  boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.055)}`,
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.72),
    borderColor: alpha(theme.palette.common.white, 0.96),
    boxShadow: `0 10px 28px ${alpha(theme.palette.common.black, 0.08)}`,
  },
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  borderRadius: 999,
  textTransform: 'none',
  minHeight: 44,
  paddingLeft: theme.spacing(2.8),
  paddingRight: theme.spacing(2.8),
  fontWeight: 700,
  color: theme.palette.primary.contrastText,
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.96)}, ${alpha(theme.palette.primary.dark, 0.96)})`,
  boxShadow: `0 10px 28px ${alpha(theme.palette.primary.main, 0.22)}`,
  '&:hover': {
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
    boxShadow: `0 13px 34px ${alpha(theme.palette.primary.main, 0.30)}`,
    transform: 'translateY(-1px)',
  },
  '&:disabled': {
    color: alpha(theme.palette.primary.contrastText, 0.78),
    background: alpha(theme.palette.primary.main, 0.48),
  },
}));

// ============================================
// ОСНОВНОЙ КОМПОНЕНТ
// ============================================

const CreateEditNewsModal = ({
  open,
  onClose,
  eventId,
  sections = [],
  currentSectionId = null,
  newsToEdit = null,
  onSuccess
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});

  // Данные формы
  const [formData, setFormData] = useState({
    section: null,
    title: '',
    excerpt: '',
    content: ''
  });

  // Файлы для загрузки (временные, для отображения)
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingMedia, setExistingMedia] = useState([]);

  // Валидация
  const [errors, setErrors] = useState({});

  // 🔥 ИСПРАВЛЕНО: при открытии сбрасываем все состояния
  useEffect(() => {
    if (open) {
      // Сбрасываем состояния
      setSuccess(false);
      setError('');
      setLoading(false);
      setUploadProgress({});

      if (newsToEdit) {
        // Режим редактирования
        setFormData({
          section: newsToEdit.section || null,
          title: newsToEdit.title || '',
          excerpt: newsToEdit.excerpt || '',
          content: newsToEdit.content || ''
        });
        setExistingMedia(newsToEdit.media || []);
        setSelectedFiles([]);
      } else {
        // Режим создания
        setFormData({
          // 🔥 ВАЖНО: используем currentSectionId для предварительного выбора
          section: currentSectionId || null,
          title: '',
          excerpt: '',
          content: ''
        });
        setSelectedFiles([]);
        setExistingMedia([]);
      }
      setErrors({});
    }
  }, [open, newsToEdit, currentSectionId]);

  // Валидация формы
  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Введите заголовок новости';
    }

    if (!formData.content.trim()) {
      newErrors.content = 'Введите содержание новости';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Обработка выбора файлов
  const onDrop = (acceptedFiles) => {
    // Добавляем новые файлы к уже выбранным
    const newFiles = acceptedFiles.map(file => ({
      file,
      id: `new-${Date.now()}-${Math.random()}`,
      name: file.name,
      size: file.size,
      type: file.type,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    }));

    setSelectedFiles(prev => [...prev, ...newFiles]);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
      'video/*': ['.mp4', '.webm', '.ogg', '.mov']
    },
    maxSize: 50 * 1024 * 1024, // 50MB для видео
    maxFiles: 10
  });

  const handleRemoveNewFile = (fileId) => {
    setSelectedFiles(prev => {
      const fileToRemove = prev.find(f => f.id === fileId);
      if (fileToRemove?.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prev.filter(f => f.id !== fileId);
    });
  };

  const handleRemoveExistingMedia = async (mediaId) => {
    if (!newsToEdit) return;

    try {
      await newsAPI.deleteMedia(newsToEdit.id, mediaId);
      setExistingMedia(prev => prev.filter(m => m.id !== mediaId));
    } catch (err) {
      console.error('Ошибка удаления медиа:', err);
      setError('Не удалось удалить файл');
    }
  };

  // Подготовка FormData для отправки (универсальная — и для создания, и для редактирования)
const prepareFormData = () => {
    const formDataObj = new FormData();

    // event — только при создании (при редактировании не меняется)
    if (!newsToEdit) {
      formDataObj.append('event', eventId);
    }

    // Секция
    if (formData.section) {
      if (typeof formData.section === 'object' && formData.section?.id) {
        formDataObj.append('section', formData.section.id);
      } else if (typeof formData.section === 'number') {
        formDataObj.append('section', formData.section);
      }
    }

    formDataObj.append('title', formData.title);
    formDataObj.append('content', formData.content);

    if (formData.excerpt) {
      formDataObj.append('excerpt', formData.excerpt);
    }

    // Для редактирования: передаём ID существующих медиа, которые остались
    if (newsToEdit && existingMedia.length > 0) {
      const existingMediaIds = existingMedia.map(m => m.id);
      formDataObj.append('existing_media_ids', JSON.stringify(existingMediaIds));
    }

    // Новые файлы
    selectedFiles.forEach((fileObj) => {
      formDataObj.append('media', fileObj.file);
    });

    // Отладка
    console.log('📦 Отправляемые данные:');
    for (let pair of formDataObj.entries()) {
      if (pair[0] === 'media' && pair[1] instanceof File) {
        console.log(`   ${pair[0]}: ${pair[1].name} (${pair[1].size} bytes)`);
      } else {
        console.log(`   ${pair[0]}: ${pair[1]}`);
      }
    }

    return formDataObj;
};

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const formDataObj = prepareFormData();

      if (newsToEdit) {
        // 🔥 Режим редактирования — ОТПРАВЛЯЕМ FormData (multipart/form-data)
        const response = await newsAPI.updateNewsWithFiles(newsToEdit.id, formDataObj);
        setSuccess(true);
        setTimeout(() => {
          onClose();
          if (onSuccess) onSuccess(response);
        }, 1500);
      } else {
        // Режим создания — FormData (multipart/form-data)
        const response = await newsAPI.createNews(formDataObj);
        console.log('✅ Ответ от сервера:', response);
        setSuccess(true);
        setTimeout(() => {
          onClose();
          if (onSuccess) onSuccess(response);
        }, 1500);
      }
    } catch (err) {
      console.error('❌ Ошибка сохранения новости:', err);
      setError(err.response?.data?.error || err.response?.data?.detail || 'Не удалось сохранить новость');
      setLoading(false);
    }
};

  const handleFormCancel = () => {
    // Очищаем preview URL'ы
    selectedFiles.forEach(file => {
      if (file.preview) {
        URL.revokeObjectURL(file.preview);
      }
    });
    onClose();
  };

  const getMediaIcon = (type) => {
    if (type?.startsWith('video/')) return <MovieIcon />;
    return <ImageIcon />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const isVideo = (file) => {
    return file.type?.startsWith('video/') ||
           file.name?.toLowerCase().includes('.mov') ||
           file.name?.toLowerCase().includes('.mp4');
  };

  return (
    <StyledDialog open={open} onClose={handleFormCancel} maxWidth="md" fullWidth>
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              flexShrink: 0,
              display: 'grid',
              placeItems: 'center',
              borderRadius: '50%',
              color: 'primary.main',
              backgroundColor: alpha(theme.palette.primary.main, 0.11),
              border: `1px solid ${alpha(theme.palette.common.white, 0.76)}`,
              boxShadow: `0 8px 22px ${alpha(theme.palette.primary.main, 0.10)}`,
            }}
          >
            <ArticleIcon fontSize="small" />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 650,
                fontSize: { xs: '1.15rem', sm: '1.3rem' },
                lineHeight: 1.25,
                color: 'text.primary',
              }}
            >
              {newsToEdit ? 'Редактирование новости' : 'Создание новости'}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mt: 0.35,
                color: 'text.secondary',
                fontWeight: 350,
                fontSize: '0.82rem',
              }}
            >
              {newsToEdit ? 'Измените содержание и медиафайлы новости' : 'Создайте публикацию для мероприятия'}
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={handleFormCancel}
          size="small"
          sx={{
            ml: 2,
            flexShrink: 0,
            width: 38,
            height: 38,
            color: 'text.secondary',
            backgroundColor: alpha(theme.palette.common.white, 0.42),
            border: `1px solid ${alpha(theme.palette.common.white, 0.78)}`,
            boxShadow: `0 6px 18px ${alpha(theme.palette.common.black, 0.05)}`,
            '&:hover': {
              color: 'text.primary',
              backgroundColor: alpha(theme.palette.common.white, 0.70),
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </StyledDialogTitle>

      <StyledDialogContent>
        {success && (
          <Alert
            severity="success"
            sx={{
              mb: 2.2,
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.success.main, 0.18)}`,
              background: `linear-gradient(145deg, ${alpha(theme.palette.success.light, 0.22)}, ${alpha(theme.palette.common.white, 0.34)})`,
              backdropFilter: 'blur(14px)',
              WebkitBackdropFilter: 'blur(14px)',
              boxShadow: `0 10px 28px ${alpha(theme.palette.common.black, 0.05)}`,
            }}
          >
            Новость успешно сохранена!
          </Alert>
        )}

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2.2,
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.error.main, 0.18)}`,
              background: `linear-gradient(145deg, ${alpha(theme.palette.error.light, 0.18)}, ${alpha(theme.palette.common.white, 0.34)})`,
              backdropFilter: 'blur(14px)',
              WebkitBackdropFilter: 'blur(14px)',
              boxShadow: `0 10px 28px ${alpha(theme.palette.common.black, 0.05)}`,
            }}
          >
            {error}
          </Alert>
        )}

        {!currentSectionId && sections.length > 0 && (
          <GlassSection sx={{ mt: 0.5, mb: 2.2 }}>
            <SectionLabel>Раздел мероприятия</SectionLabel>
            <FormControl fullWidth>
              <InputLabel>Привязка к секции (необязательно)</InputLabel>
              <Select
                value={formData.section?.id || formData.section || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    setFormData({ ...formData, section: null });
                  } else {
                    const section = sections.find(s => s.id === value);
                    setFormData({ ...formData, section: section || value });
                  }
                }}
                label="Привязка к секции (необязательно)"
                sx={{
                  borderRadius: 3,
                  backgroundColor: alpha(theme.palette.common.white, 0.42),
                  backdropFilter: 'blur(14px)',
                  WebkitBackdropFilter: 'blur(14px)',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.common.white, 0.78),
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.primary.main, 0.28),
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.primary.main, 0.56),
                  },
                }}
              >
                <MenuItem value="">
                  <em>Общая новость (без секции)</em>
                </MenuItem>
                {sections.map((section) => (
                  <MenuItem key={section.id} value={section.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: section.color || theme.palette.primary.main,
                          boxShadow: `0 0 0 4px ${alpha(section.color || theme.palette.primary.main, 0.08)}`,
                        }}
                      />
                      {section.title}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText sx={{ ml: 0.5 }}>
                Оставьте пустым для общей новости мероприятия
              </FormHelperText>
            </FormControl>
          </GlassSection>
        )}

        <GlassSection sx={{ mt: 1, mb: 2.2 }}>
          <SectionLabel>Основная информация</SectionLabel>

          <GlassField
            fullWidth
            label="Заголовок новости *"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            error={!!errors.title}
            helperText={errors.title}
            sx={{ mb: 2.2 }}
          />

          <GlassField
            fullWidth
            label="Краткое описание (необязательно)"
            value={formData.excerpt}
            onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
            multiline
            rows={2}
            placeholder="Краткий текст для предпросмотра (если не указано, будет взято из начала содержания)"
            sx={{ mb: 2.2 }}
          />

          <GlassField
            fullWidth
            label="Содержание новости *"
            value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            multiline
            rows={7}
            error={!!errors.content}
            helperText={errors.content}
          />
        </GlassSection>

        <GlassSection sx={{ mb: 2.2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1.8 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                display: 'grid',
                placeItems: 'center',
                borderRadius: '50%',
                color: 'primary.main',
                backgroundColor: alpha(theme.palette.primary.main, 0.10),
              }}
            >
              <UploadIcon fontSize="small" />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: '0.98rem' }}>
                Медиафайлы
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 350, fontSize: '0.78rem' }}>
                Изображения и видео можно добавить перетаскиванием или через выбор файлов
              </Typography>
            </Box>
          </Box>

          <DropzoneArea
            {...getRootProps()}
            isdragactive={isDragActive ? 'true' : 'false'}
          >
            <input {...getInputProps()} />
            <Box
              sx={{
                width: 58,
                height: 58,
                mx: 'auto',
                mb: 1.5,
                display: 'grid',
                placeItems: 'center',
                borderRadius: '50%',
                color: 'primary.main',
                backgroundColor: alpha(theme.palette.primary.main, isDragActive ? 0.15 : 0.09),
                border: `1px solid ${alpha(theme.palette.common.white, 0.72)}`,
                boxShadow: `0 10px 26px ${alpha(theme.palette.primary.main, 0.08)}`,
              }}
            >
              <UploadIcon sx={{ fontSize: 27 }} />
            </Box>
            <Typography sx={{ fontWeight: 600, fontSize: '0.92rem' }}>
              {isDragActive ? 'Отпустите файлы здесь' : 'Перетащите файлы сюда'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.78rem' }}>
              или кликните для выбора · изображения и видео · до 50 MB
            </Typography>
          </DropzoneArea>
        </GlassSection>

        {Object.keys(uploadProgress).length > 0 && (
          <GlassSection sx={{ mb: 2.2 }}>
            <SectionLabel>Загрузка</SectionLabel>
            {Object.entries(uploadProgress).map(([fileName, progress]) => (
              <Box key={fileName} sx={{ mb: 1.2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.6 }}>
                  <Typography variant="caption" noWrap sx={{ maxWidth: '80%', color: 'text.secondary' }}>
                    {fileName}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {progress}%
                  </Typography>
                </Box>
                <CircularProgress variant="determinate" value={progress} size={24} sx={{ display: 'block' }} />
              </Box>
            ))}
          </GlassSection>
        )}

        {existingMedia.length > 0 && (
          <GlassSection sx={{ mb: 2.2 }}>
            <SectionLabel>Загруженные файлы</SectionLabel>
            <Grid container spacing={1.6}>
              {existingMedia.map((media) => (
                <Grid item xs={6} sm={4} key={media.id}>
                  <MediaCard>
                    {media.type === 'video' ? (
                      <Box
                        sx={{
                          height: 120,
                          background: `linear-gradient(145deg, ${alpha(theme.palette.grey[900], 0.94)}, ${alpha(theme.palette.primary.dark, 0.72)})`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                        }}
                      >
                        <MovieIcon sx={{ fontSize: 38, opacity: 0.86 }} />
                      </Box>
                    ) : (
                      <CardMedia component="img" height="120" image={media.url} alt="" sx={{ objectFit: 'cover' }} />
                    )}
                    <DeleteOverlay className="delete-overlay">
                      <IconButton
                        onClick={() => handleRemoveExistingMedia(media.id)}
                        sx={{
                          width: 42,
                          height: 42,
                          color: 'white',
                          backgroundColor: alpha(theme.palette.error.main, 0.84),
                          '&:hover': { backgroundColor: theme.palette.error.main },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </DeleteOverlay>
                    <Box sx={{ p: 1.2 }}>
                      <Typography variant="caption" display="block" noWrap sx={{ fontWeight: 600 }}>
                        {media.name || media.filename}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(media.size)}
                      </Typography>
                    </Box>
                  </MediaCard>
                </Grid>
              ))}
            </Grid>
          </GlassSection>
        )}

        {selectedFiles.length > 0 && (
          <GlassSection sx={{ mb: 0.5 }}>
            <SectionLabel>Новые файлы для загрузки</SectionLabel>
            <Grid container spacing={1.6}>
              {selectedFiles.map((file) => (
                <Grid item xs={6} sm={4} key={file.id}>
                  <MediaCard>
                    {isVideo(file) ? (
                      <Box
                        sx={{
                          height: 120,
                          background: `linear-gradient(145deg, ${alpha(theme.palette.grey[900], 0.94)}, ${alpha(theme.palette.primary.dark, 0.72)})`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                        }}
                      >
                        <MovieIcon sx={{ fontSize: 38, opacity: 0.86 }} />
                      </Box>
                    ) : file.preview ? (
                      <CardMedia component="img" height="120" image={file.preview} alt="" sx={{ objectFit: 'cover' }} />
                    ) : (
                      <Box
                        sx={{
                          height: 120,
                          background: `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.40)}, ${alpha(theme.palette.grey[200], 0.50)})`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <ImageIcon sx={{ fontSize: 38, color: 'text.secondary' }} />
                      </Box>
                    )}
                    <DeleteOverlay className="delete-overlay">
                      <IconButton
                        onClick={() => handleRemoveNewFile(file.id)}
                        sx={{
                          width: 42,
                          height: 42,
                          color: 'white',
                          backgroundColor: alpha(theme.palette.error.main, 0.84),
                          '&:hover': { backgroundColor: theme.palette.error.main },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </DeleteOverlay>
                    <Box sx={{ p: 1.2 }}>
                      <Typography variant="caption" display="block" noWrap sx={{ fontWeight: 600 }}>
                        {file.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(file.size)}
                      </Typography>
                    </Box>
                  </MediaCard>
                </Grid>
              ))}
            </Grid>
          </GlassSection>
        )}
      </StyledDialogContent>

      <DialogActions
        sx={{
          p: { xs: 2, sm: 2.4 },
          pt: 1.4,
          gap: 1,
          flexShrink: 0,
          background: `linear-gradient(135deg, ${alpha(theme.palette.common.white, 0.34)}, ${alpha(theme.palette.common.white, 0.12)})`,
          borderTop: `1px solid ${alpha(theme.palette.common.white, 0.58)}`,
        }}
      >
        <GlassButton onClick={handleFormCancel} startIcon={<CancelIcon />}>
          Отмена
        </GlassButton>
        <PrimaryButton
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={19} color="inherit" /> : <SaveIcon />}
        >
          {loading ? 'Сохранение...' : newsToEdit ? 'Сохранить изменения' : 'Создать новость'}
        </PrimaryButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default CreateEditNewsModal;